import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Chip,
    Collapse,
    Button,
    Divider,
    Tabs,
    Tab
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon,
    SwapHoriz as ConvertIcon
} from '@mui/icons-material';
import {
    convertWithUnits,
    copyToClipboard,
    isDecimalNumber,
    UnitConversionResult
} from '../../utils/hexConverter';

interface SmartNumberDisplayProps {
    value: string;
    label?: string;
    functionName?: string;
    contractContext?: {
        name?: string;
        symbol?: string;
        decimals?: number;
    };
    autoExpand?: boolean;
    showRawValue?: boolean;
    compact?: boolean;
    onOpenFloatingConverter?: (value: string) => void;
}

const SmartNumberDisplay: React.FC<SmartNumberDisplayProps> = ({
    value,
    label,
    functionName,
    contractContext,
    autoExpand = false,
    showRawValue = true,
    compact = false,
    onOpenFloatingConverter
}) => {
    const [result, setResult] = useState<UnitConversionResult | null>(null);
    const [isExpanded, setIsExpanded] = useState(autoExpand);
    const [copySuccess, setCopySuccess] = useState<string>('');
    const [selectedTab, setSelectedTab] = useState(0);

    // Convert value when component mounts or value changes
    useEffect(() => {
        if (value && value.trim() && isDecimalNumber(value)) {
            try {
                const conversionResult = convertWithUnits(value.trim(), functionName, contractContext);
                setResult(conversionResult);
            } catch (error) {
                setResult(null);
            }
        } else {
            setResult(null);
        }
    }, [value, functionName, contractContext]);

    // Handle copy to clipboard
    const handleCopy = async (text: string, label: string) => {
        const success = await copyToClipboard(text);
        if (success) {
            setCopySuccess(`${label} copied!`);
            setTimeout(() => setCopySuccess(''), 1500);
        }
    };

    // Don't render if no valid conversion result or low confidence
    if (!result || result.context.confidence < 0.3) {
        return null;
    }

    // Get the best format based on context
    const getBestFormat = () => {
        const { context } = result;

        if (context.type === 'token' && result.token) {
            return result.token;
        } else if (context.type === 'eth') {
            return result.eth;
        } else if (context.type === 'gwei') {
            return result.gwei;
        } else if (context.type === 'wei') {
            return result.wei;
        } else {
            return result.formatted;
        }
    };

    // Compact mode - just show best format inline
    if (compact) {
        const bestFormat = getBestFormat();

        return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                <Typography variant="caption" color="text.secondary">
                    →
                </Typography>
                <Chip
                    label={bestFormat}
                    size="small"
                    variant="outlined"
                    color={result.context.confidence > 0.7 ? 'success' : 'default'}
                    onClick={() => handleCopy(bestFormat, 'Formatted')}
                    sx={{ cursor: 'pointer', fontSize: '0.7rem', height: 20 }}
                />
                {onOpenFloatingConverter && (
                    <Tooltip title="Open in converter">
                        <IconButton
                            size="small"
                            onClick={() => onOpenFloatingConverter(value)}
                            sx={{ p: 0.25 }}
                        >
                            <ConvertIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        );
    }

    // Full mode - expandable conversion details with tabs
    return (
        <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1,
                    bgcolor: 'grey.50',
                    cursor: 'pointer'
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        label={result.context.type.toUpperCase()}
                        size="small"
                        color={result.context.confidence > 0.7 ? 'success' : result.context.confidence > 0.4 ? 'warning' : 'default'}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {label || 'Number Formats'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        ({Math.round(result.context.confidence * 100)}% confidence)
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {copySuccess && (
                        <Typography variant="caption" color="success.main">
                            {copySuccess}
                        </Typography>
                    )}
                    <IconButton size="small">
                        {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                </Box>
            </Box>

            {/* Expandable Content */}
            <Collapse in={isExpanded}>
                <Box sx={{ p: 2 }}>
                    {/* Format Tabs */}
                    <Tabs
                        value={selectedTab}
                        onChange={(_, newValue) => setSelectedTab(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ mb: 2, minHeight: 32 }}
                    >
                        <Tab label="Formatted" sx={{ minHeight: 32, py: 0.5 }} />
                        <Tab label="Compact" sx={{ minHeight: 32, py: 0.5 }} />
                        {result.context.type !== 'gas' && <Tab label="ETH" sx={{ minHeight: 32, py: 0.5 }} />}
                        {result.context.type !== 'gas' && <Tab label="Gwei" sx={{ minHeight: 32, py: 0.5 }} />}
                        {result.context.type !== 'gas' && <Tab label="Wei" sx={{ minHeight: 32, py: 0.5 }} />}
                        {result.token && <Tab label="Token" sx={{ minHeight: 32, py: 0.5 }} />}
                    </Tabs>

                    {/* Tab Content */}
                    <Box sx={{ minHeight: 60 }}>
                        {/* Formatted Tab */}
                        {selectedTab === 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, fontSize: '0.9rem' }}>
                                    {result.formatted}
                                </Typography>
                                <Tooltip title="Copy formatted">
                                    <IconButton size="small" onClick={() => handleCopy(result.formatted, 'Formatted')}>
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                        {/* Compact Tab */}
                        {selectedTab === 1 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, fontSize: '0.9rem' }}>
                                    {result.compact}
                                </Typography>
                                <Tooltip title="Copy compact">
                                    <IconButton size="small" onClick={() => handleCopy(result.compact, 'Compact')}>
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                        {/* ETH Tab */}
                        {selectedTab === 2 && result.context.type !== 'gas' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, fontSize: '0.9rem' }}>
                                    {result.eth}
                                </Typography>
                                <Tooltip title="Copy ETH">
                                    <IconButton size="small" onClick={() => handleCopy(result.eth, 'ETH')}>
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                        {/* Gwei Tab */}
                        {selectedTab === 3 && result.context.type !== 'gas' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, fontSize: '0.9rem' }}>
                                    {result.gwei}
                                </Typography>
                                <Tooltip title="Copy Gwei">
                                    <IconButton size="small" onClick={() => handleCopy(result.gwei, 'Gwei')}>
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                        {/* Wei Tab */}
                        {selectedTab === 4 && result.context.type !== 'gas' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, fontSize: '0.9rem', wordBreak: 'break-all' }}>
                                    {result.wei}
                                </Typography>
                                <Tooltip title="Copy Wei">
                                    <IconButton size="small" onClick={() => handleCopy(result.wei, 'Wei')}>
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                        {/* Token Tab */}
                        {result.token && selectedTab === (result.context.type === 'gas' ? 2 : 5) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, fontSize: '0.9rem' }}>
                                    {result.token}
                                </Typography>
                                <Tooltip title="Copy token amount">
                                    <IconButton size="small" onClick={() => handleCopy(result.token!, 'Token')}>
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>

                    {/* Raw Value */}
                    {showRawValue && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary" gutterBottom>
                                    Original:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all', fontSize: '0.8rem' }}>
                                        {result.original}
                                    </Typography>
                                    <Tooltip title="Copy original">
                                        <IconButton size="small" onClick={() => handleCopy(result.original, 'Original')}>
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </>
                    )}

                    {/* Context Suggestions */}
                    {result.context.suggestions.length > 0 && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                                Detection Notes:
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {result.context.suggestions.slice(0, 2).map((suggestion, index) => (
                                    <Typography key={index} variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        • {suggestion}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Actions */}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {onOpenFloatingConverter && (
                            <>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<ConvertIcon />}
                                    onClick={() => onOpenFloatingConverter(value)}
                                >
                                    Open Floating Converter
                                </Button>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => {
                                        // Switch to Hex Converter tab (index 7) and copy value
                                        const event = new CustomEvent('switchToConverterTab', {
                                            detail: { value, mode: 'decimal-to-hex' }
                                        });
                                        window.dispatchEvent(event);
                                    }}
                                >
                                    Go to Hex Converter Tab
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

export default SmartNumberDisplay;
